import {
    APIResponse,
    CompanyFinancialDto,
    OrdersTabsType,
    OrderStatus,
} from "@/shared/api-contract/api-contract";
import { useI18n } from "@/shared/composables/use-i18n";
import { setup } from "vue-class-component";
import { Model, Prop, Vue } from "vue-property-decorator";
import { useRouter } from "vue-router";
import { watchEffect } from "vue";
import { eff } from "@/core/utils/effect-ts-abbr";
import { appAxios } from "@/core/utils/appAxios";
import { currentUserModel } from "@/shared/state/current-user.model";
import { brandLabelName } from "@/themes";

export type Tab = {
    orderStatus: OrderStatus;
    value: number;
    path: string;
    title: string;
};

export class OrdersTabs extends Vue {
    @Model("activeDashboardItem", { required: true })
    activeItem!: OrderStatus;
    @Prop({ required: true })
    orderDetails!: OrdersTabsType;
    router = setup(useRouter);

    i18n = setup(useI18n);
    ordersStatus = Object.fromEntries(Object.keys(OrderStatus).map((el) => [el, el]));
    reverseOrdersStatus = Object.fromEntries(
        Object.entries(OrderStatus).map(([key, val]) => [val, key]),
    );
    orderStatusEnum = OrderStatus;
    tabsInfo: readonly Tab[] = [];

    companyFinancialDto: CompanyFinancialDto | null = null;
    get creditLimit() {
        return this.companyFinancialDto?.credit_limit_euro !== undefined
            ? new Intl.NumberFormat().format(this.companyFinancialDto?.credit_limit_euro)
            : " - ";
    }
    get creditRemaining() {
        return this.companyFinancialDto?.credit_remaining_euro !== undefined
            ? new Intl.NumberFormat().format(
                  this.companyFinancialDto?.credit_remaining_euro,
              )
            : " - ";
    }
    updateTabOrder(on_credit: boolean) {
        const sorting = eff.ord.makeOrd<Tab>(() => {
            return on_credit ? -1 : 1;
        });
        this.tabsInfo = eff.fn.pipe(
            [
                {
                    orderStatus: OrderStatus.to_pay,
                    value: this.orderDetails!.to_pay?.orders_counter,
                    path: this.ordersStatus.to_pay,
                    title: "myOrders.to_pay.title",
                },
                {
                    orderStatus: OrderStatus.to_transport,
                    value: this.orderDetails!.to_transport?.orders_counter,
                    path: this.ordersStatus.to_transport,
                    title: "myOrders.to_transport.title",
                },
            ],
            eff.array.sort(sorting),
        );
    }
    async mounted() {
        this.updateTabOrder(false);
        await this.fetchCompanyFinancialData();
        // this.orderDetails = Object.fromEntries(this.tabsStats.map(tab => [OrderStatus[tab.status.id], tab]))
        watchEffect(() => {
            const params = this.router.currentRoute.params;
            this.activeItem = (params.order_status as OrderStatus) || null;
        });
    }

    getValue(tab: Tab, action_needed?: boolean): number | undefined {
        const { orderStatus } = tab;
        if (!this.orderDetails?.[this.reverseOrdersStatus?.[orderStatus]]) return;
        switch (orderStatus) {
            case OrderStatus.to_transport:
            case OrderStatus.to_pay:
                return action_needed
                    ? this.orderDetails?.[this.reverseOrdersStatus?.[orderStatus]]
                          ?.orders_action_required_counter
                    : this.orderDetails?.[this.reverseOrdersStatus?.[orderStatus]]
                          ?.orders_counter;
            default:
                return 0;
        }
    }

    /* API Connectivity --------------------------------------------------------------------------------------------- */
    async fetchCompanyFinancialData() {
        const isCompanyAvailible = currentUserModel.userDto.value?.id_company;
        if (!isCompanyAvailible) return;
        await appAxios.value
            .get<APIResponse<CompanyFinancialDto>>(`/client/web/company/financial`)
            .then((response) => {
                if (response.status === 200 && response.data.status.code === 200) {
                    this.companyFinancialDto = eff.fn.pipe(response.data.data);
                    this.updateTabOrder(response.data.data.default_order_flow.on_credit);
                }
            })
            .catch(console.log);
    }

    get noAvailableForTransportText() {
        return this.i18n.t("myOrders.noAvailableForTransport", {
            brandLabelName,
        });
    }
}
